import { txt } from '../utils/dom.js';
import { clasesImagenRecorte } from '../utils/imagen-recorte.js';
import { atributosEncuadre } from '../utils/encuadre.js';
import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { getIcon } from '../utils/icons-library.js';
import { altAttr } from '../utils/alt-text.js';
import { t } from '../utils/i18n.js';

    export const featuresBlock = (rawProps) => {
        const p = safe(rawProps);
        // `safe()` (utils/contrast.js) YA dejó `p.items` en un array, así que este `|| []` es
        // cinturón, NO un default de contenido: con 0 items este bloque se pinta VACÍO (titular +
        // contenedor sin nada, y sin ningún "+" con el que añadir desde el lienzo). De que no
        // llegue a publicarse así se encarga la guarda de CPU `_fix_bloques_sin_contenido`
        // (llm_sanitizers.py::_ARRAY_DE_CONTENIDO), que descarta el bloque al generar.
        const items = p.items || [];
        const placeholder = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

        // 1. CÁLCULO DE CONTRASTE INTELIGENTE PARA LOS BOTONES
        const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
        const btnBg = isDark ? "#ffffff" : "#111827";
        const btnIcon = isDark ? "#111827" : "#ffffff";

        // 2. LÓGICA DE IMÁGENES
        let hasImageIntent = p.use_image && (p.image_url || p.image_keyword);
        let query = p.image_url || p.image_keyword || "";

        let containerStyle = "";
        let cardStyle = "";
        let numberStyle = "";
        let alignStyle = "left";

        // DUOTONO DE MARCA: el MEDALLÓN del icono/número (v3) y el filo izquierdo (v2) son
        // decoración, no CTA → estrenan el segundo color de marca cuando el cliente lo tiene.
        // `var(--secondary, var(--accent))` cae al acento si no hay secundario ⇒ render idéntico.
        // El botón de la tarjeta sigue con --accent (los CTA no se tocan).
        const DUO = 'var(--secondary, var(--accent))';

        // 3. LÓGICA DE VARIANTES VISUALES (v1, v2, v3)
        if (p.variant === 'v2') {
            cardStyle = `background: transparent; padding: 20px 0 20px 25px; border-left: 3px solid ${DUO}; display: flex; flex-direction: column; position: relative;`;
            numberStyle = `opacity: 0.2; font-weight: 900; margin-bottom: 5px; font-size: 2.5rem; line-height: 1; font-family: serif;`;
        } else if (p.variant === 'v3') {
            cardStyle = `background: var(--card-bg, color-mix(in srgb, var(--text) 4%, transparent)); padding: 40px 30px; border-radius: var(--card-radius, 30px); display: flex; flex-direction: column; align-items: center; text-align: center; position: relative;`;
            numberStyle = `background: color-mix(in srgb, ${DUO} 13%, transparent); color: ${DUO}; width: 50px; height: 50px; display: flex; align-items: center; justify-content: center; border-radius: 50%; font-weight: 900; margin-bottom: 20px; font-size: 1.2rem; flex-shrink: 0;`;
            alignStyle = "center";
        } else {
            cardStyle = `background: var(--card-bg, color-mix(in srgb, var(--text) 8%, transparent)); padding: 30px; border-radius: var(--card-radius, 20px); border: var(--card-border, 1px solid color-mix(in srgb, var(--text) 20%, transparent)); display: flex; flex-direction: column; position: relative;`;
            numberStyle = `color: var(--accent); font-weight: 900; margin-bottom: 15px; font-size: 1.3rem;`;
        }

        // 4. LÓGICA DE RESPONSIVIDAD (Grid vs Scroll) CORREGIDA
        if (hasImageIntent) {
            if (items.length > 4) {
                containerStyle = `display: grid; grid-template-rows: 1fr 1fr; grid-auto-flow: column; grid-auto-columns: min(280px, 80cqw); gap: 20px; overflow-x: auto; padding-bottom: 20px; scroll-snap-type: x proximity; justify-content: start;`;
                cardStyle += " scroll-snap-align: start;";
            } else {
                containerStyle = `display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; justify-content: center;`;
            }
        } else {
            // Aumentamos el límite a 4 para que no se rompa el centrado
            if (items.length > 4) {
                containerStyle = `display: flex; flex-wrap: nowrap; overflow-x: auto; gap: 30px; padding: 20px 10px 50px 10px; scroll-snap-type: x proximity; width: 100%; align-items: stretch; justify-content: start;`;
                cardStyle += " flex: 0 0 auto; width: 350px; max-width: 85cqw; scroll-snap-align: center;";
            } else {
                // Esto centrará automáticamente 1, 2, 3 y 4 elementos
                containerStyle = `display: flex; flex-wrap: wrap; justify-content: center; gap: 40px; width: 100%; align-items: stretch;`;
                cardStyle += " flex: 1 1 300px; max-width: 400px;";
            }
        }

        const titleText = (p.title || "").toLowerCase();
        const hideNumbers = titleText.includes('menú') || titleText.includes('snack');

        // 5. RENDERIZADO DEL HTML
        // Accesibilidad: el icono (o el ordinal "01") de cada tarjeta es DECORATIVO — lo que dice la
        // tarjeta está en su <h3>/<p> justo debajo — así que va con aria-hidden para que el lector de
        // pantalla no recite "cero uno" antes de cada característica. La imagen lateral sí describe
        // algo, y su alt sale del contenido del bloque (utils/a11y.js), nunca de un texto fijo.
        return `
    <section id="bloque-features" class="reveal variant-${p.variant || 'v1'}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding: 100px 8%;">
      <div class="ambient-glow-layer"></div>
      <style>
        .feature-slider::-webkit-scrollbar { display: none; }
        .feature-slider { -ms-overflow-style: none; scrollbar-width: none; }
        @media (max-width: 992px) {
            .features-flex-container { justify-content: center !important; text-align: center !important; }
            .features-image-side { margin: 40px auto 0 auto !important; order: 2; }
            .features-text-side { order: 1; flex: 1 1 100% !important; text-align: center !important; }
            .feature-slider { justify-content: center; }
        }
      </style>
      
      <div class="features-flex-container" style="display: flex; gap: 60px; flex-wrap: wrap; align-items: flex-start;">
        <div class="features-text-side" style="flex: ${hasImageIntent ? '1.2' : '1'}; min-width: min(100%, 300px); max-width: 100%; overflow: visible; text-align: ${alignStyle};">
            <h2 data-field="title" contenteditable="true" style="font-size: calc(clamp(2.5rem, 5cqw, 3rem) * var(--type-scale, 1)); font-weight: 800; margin-bottom: 50px;">${p.title || 'Nuestras Características'}</h2>
            
            <div class="feature-slider" style="${containerStyle}">
                ${items.map((item, i) => `
                <div class="card feature-card sortable-item stagger-item" data-item-index="${i}" data-array-name="items" style="--stagger-i: ${i}; ${cardStyle}">
                    ${p.variant === 'v2' ? `<span class="ghost-number" aria-hidden="true" style="font-size: 7rem; top: 10px; right: 10px;">0${i + 1}</span>` : ''}
                    
                    ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="items" data-index="${i}">+</button>` : ''}
                    <button class="item-add-btn card-add-right" data-array="items" data-index="${i}">+</button>

                    <div class="item-tools">
                        <button class="tool-btn icon-picker-btn" data-array="items" data-index="${i}" title="Cambiar icono" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.palette || '🎨'}</button>
                        <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
                        <button class="tool-btn item-delete-btn" data-array="items" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
                    </div>

                    ${!hideNumbers ? `<div class="block-icon-svg" aria-hidden="true" style="${numberStyle}">${item.icon ? getIcon(item.icon, p.variant === 'v3' ? 24 : 28) : `0${i + 1}`}</div>` : ''}
                    <h3 data-field="name" contenteditable="true" style="font-size: calc(1.2rem * var(--heading-scale, var(--u-type-scale, 1))); font-weight: 700; margin-bottom: 10px;">${txt(item.name)}</h3>
                    <p data-field="desc" contenteditable="true" style="opacity: 0.7; font-size: calc(0.95rem * var(--text-scale, 1)); flex-grow: 1;">${txt(item.desc)}</p>
                </div>
                `).join('')}
            </div>
        </div>

        ${hasImageIntent ? `
        <div class="features-image-side" style="display: none; flex: 0.8; min-width: min(100%, 300px); max-width: 500px; position: sticky; top: 100px;">
            <img src="${placeholder}" ${altAttr(p.image_alt, p.title, p.image_keyword)} data-unsplash="${query}" class="unsplash-target${clasesImagenRecorte(p)}${atributosEncuadre(p).clase}" style="width: 100%; border-radius: var(--card-radius, 24px); box-shadow: var(--shadow-card, 0 20px 40px rgba(0,0,0,0.1)); object-fit: cover; aspect-ratio: 1/1; transition: opacity 0.5s; opacity: 0;${atributosEncuadre(p).estilo}">
            <div class="loading-label" style="margin-top: 20px; font-size: 0.8rem; opacity: 0.5; text-align: center; font-style: italic;">${t('loading_asset')}</div>
        </div>` : ''}
      </div>
    </section>`;
    }
